import { collection, getDocs, getDoc, doc, query, where, addDoc, updateDoc, deleteDoc, orderBy, limit, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

// Define TypeScript interfaces for our data models
export interface Product {
  id: string;
  name: string;
  price: number;
  discountedPrice?: number | null;
  image: string;
  images?: string[];
  rating?: number;
  discount?: number;
  isNew?: boolean;
  category: string;
  stock: number;
  description?: string;
  features?: string[];
  stockCode?: string;
  reviews?: Review[];
}

export interface Review {
  id: string;
  user: string;
  rating: number;
  comment: string;
  date?: Date;
}

export interface Category {
  id: string;
  name: string;
  image: string;
}

export interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content?: string;
  image: string;
  date?: Date;
  author?: string;
}

export interface SiteSettings {
  id: string;
  siteTitle: string;
  siteDescription: string;
  logoUrl: string;
  faviconUrl: string;
  freeShippingThreshold: number;
  orderProcessingTime: string;
  contactEmail: string;
  contactPhone: string;
  currency?: string;
  currencySymbol?: string;
  defaultLanguage?: string;
  analyticsId?: string;
  facebookPixelId?: string;
  googleTagManagerId?: string;
  newsletterEnabled?: boolean;
  googleAnalyticsId?: string;
  privacyPolicy?: string;
  termsOfService?: string;
  refundPolicy?: string;
}

// Initialize site settings if it doesn't exist
export const initializeSiteSettings = async () => {
  try {
    const settingsDocRef = doc(db, 'settings', 'site');
    const settingsDoc = await getDoc(settingsDocRef);
    
    if (!settingsDoc.exists()) {
      // Create default settings
      const defaultSettings = {
        siteTitle: 'Rölyef Boyama Setleri',
        siteDescription: 'Kaliteli ve eğlenceli boya setleri',
        logoUrl: '',
        faviconUrl: '',
        freeShippingThreshold: 750,
        orderProcessingTime: '2-3 iş günü',
        contactEmail: '',
        contactPhone: '',
        currency: 'TRY',
        currencySymbol: '₺',
        defaultLanguage: 'tr',
        analyticsId: '',
        facebookPixelId: '',
        googleTagManagerId: '',
        newsletterEnabled: true,
        googleAnalyticsId: '',
        privacyPolicy: '',
        termsOfService: '',
        refundPolicy: ''
      };
      
      await setDoc(settingsDocRef, defaultSettings);
      console.log('Default site settings created successfully');
    } else {
      console.log('Site settings already exist');
    }
  } catch (error) {
    console.error('Error initializing site settings:', error);
    throw error;
  }
};

// Product operations
export const getProducts = async (): Promise<Product[]> => {
  try {
    const productsCollection = collection(db, 'products');
    const productsSnapshot = await getDocs(productsCollection);
    
    return productsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Product[];
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

export const getProductById = async (id: string): Promise<Product | null> => {
  try {
    const productDoc = doc(db, 'products', id);
    const productSnapshot = await getDoc(productDoc);
    
    if (productSnapshot.exists()) {
      return {
        id: productSnapshot.id,
        ...productSnapshot.data()
      } as Product;
    }
    return null;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

export const getProductsByCategory = async (category: string): Promise<Product[]> => {
  try {
    const productsCollection = collection(db, 'products');
    const q = query(productsCollection, where('category', '==', category));
    const productsSnapshot = await getDocs(q);
    
    return productsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Product[];
  } catch (error) {
    console.error('Error fetching products by category:', error);
    throw error;
  }
};

export const getFeaturedProducts = async (limitCount: number = 8): Promise<Product[]> => {
  try {
    const productsCollection = collection(db, 'products');
    const q = query(productsCollection, orderBy('rating', 'desc'), limit(limitCount));
    const productsSnapshot = await getDocs(q);
    
    return productsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Product[];
  } catch (error) {
    console.error('Error fetching featured products:', error);
    throw error;
  }
};

// Category operations
export const getCategories = async (): Promise<Category[]> => {
  try {
    const categoriesCollection = collection(db, 'categories');
    const categoriesSnapshot = await getDocs(categoriesCollection);
    
    return categoriesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Category[];
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

// Slider operations
export const getSliders = async (): Promise<any[]> => {
  try {
    const slidersCollection = collection(db, 'sliders');
    const slidersSnapshot = await getDocs(slidersCollection);
    
    return slidersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching sliders:', error);
    throw error;
  }
};

// Blog operations
export const getBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    const blogCollection = collection(db, 'blogPosts');
    const blogSnapshot = await getDocs(blogCollection);
    
    return blogSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as BlogPost[];
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
};

export const getBlogPostById = async (id: string): Promise<BlogPost | null> => {
  try {
    const blogDoc = doc(db, 'blogPosts', id);
    const blogSnapshot = await getDoc(blogDoc);
    
    if (blogSnapshot.exists()) {
      return {
        id: blogSnapshot.id,
        ...blogSnapshot.data()
      } as BlogPost;
    }
    return null;
  } catch (error) {
    console.error('Error fetching blog post:', error);
    throw error;
  }
};

// Reviews operations
export const getReviewsByProduct = async (productId: string): Promise<Review[]> => {
  try {
    const reviewsCollection = collection(db, 'reviews');
    const q = query(reviewsCollection, where('productId', '==', productId));
    const reviewsSnapshot = await getDocs(q);
    
    return reviewsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Review[];
  } catch (error) {
    console.error('Error fetching reviews:', error);
    throw error;
  }
};

export const addReview = async (reviewData: Omit<Review, 'id'> & { productId: string }) => {
  try {
    const reviewsCollection = collection(db, 'reviews');
    const docRef = await addDoc(reviewsCollection, {
      ...reviewData,
      date: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding review:', error);
    throw error;
  }
};

// Check if user has purchased a product
export const hasUserPurchasedProduct = async (userId: string, productId: string): Promise<boolean> => {
  try {
    // Check if the user has any orders containing this product
    const ordersCollection = collection(db, 'orders');
    const q = query(ordersCollection, where('userId', '==', userId));
    const ordersSnapshot = await getDocs(q);
    
    for (const orderDoc of ordersSnapshot.docs) {
      const orderData = orderDoc.data();
      const orderItems = orderData.items || [];
      
      // Check if the product exists in any of the user's orders
      const hasProductInOrder = orderItems.some((item: any) => item.id === productId);
      if (hasProductInOrder) {
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking if user purchased product:', error);
    return false;
  }
};

// Site settings operations
export const getSiteSettings = async (): Promise<SiteSettings> => {
  try {
    const settingsDoc = doc(db, 'settings', 'site');
    const settingsSnapshot = await getDoc(settingsDoc);
    
    if (settingsSnapshot.exists()) {
      return {
        id: settingsSnapshot.id,
        ...settingsSnapshot.data()
      } as SiteSettings;
    } else {
      // Return default settings if none exist
      return {
        id: 'site',
        siteTitle: 'Rölyef Boyama Setleri',
        siteDescription: 'Kaliteli ve eğlenceli boya setleri',
        logoUrl: '',
        faviconUrl: '',
        freeShippingThreshold: 750,
        orderProcessingTime: '2-3 iş günü',
        contactEmail: '',
        contactPhone: '',
        currency: 'TRY',
        currencySymbol: '₺',
        defaultLanguage: 'tr',
        analyticsId: '',
        facebookPixelId: '',
        googleTagManagerId: '',
        newsletterEnabled: true,
        googleAnalyticsId: '',
        privacyPolicy: '',
        termsOfService: '',
        refundPolicy: ''
      };
    }
  } catch (error) {
    console.error('Error fetching site settings:', error);
    // Return default settings in case of error
    return {
      id: 'site',
      siteTitle: 'Rölyef Boyama Setleri',
      siteDescription: 'Kaliteli ve eğlenceli boya setleri',
      logoUrl: '',
      faviconUrl: '',
      freeShippingThreshold: 750,
      orderProcessingTime: '2-3 iş günü',
      contactEmail: '',
      contactPhone: '',
      currency: 'TRY',
      currencySymbol: '₺',
      defaultLanguage: 'tr',
      analyticsId: '',
      facebookPixelId: '',
      googleTagManagerId: '',
      newsletterEnabled: true,
      googleAnalyticsId: '',
      privacyPolicy: '',
      termsOfService: '',
      refundPolicy: ''
    };
  }
};

export const updateSiteSettings = async (settings: Partial<SiteSettings>): Promise<void> => {
  try {
    // Filter out undefined values to prevent Firestore update errors
    const filteredSettings: any = {};
    Object.keys(settings).forEach(key => {
      const value = settings[key as keyof Partial<SiteSettings>];
      if (value !== undefined) {
        filteredSettings[key] = value;
      }
    });

    const settingsDoc = doc(db, 'settings', 'site');
    await updateDoc(settingsDoc, filteredSettings);
  } catch (error) {
    console.error('Error updating site settings:', error);
    throw error;
  }
};

// Testimonial operations
export interface Testimonial {
  id: string;
  name: string;
  comment: string;
  rating: number;
  isActive: boolean;
}

export const getTestimonials = async (): Promise<Testimonial[]> => {
  try {
    const testimonialsCollection = collection(db, 'testimonials');
    const q = query(testimonialsCollection, where('isActive', '==', true));
    const testimonialsSnapshot = await getDocs(q);
    
    return testimonialsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Testimonial[];
  } catch (error) {
    console.error('Error fetching testimonials:', error);
    throw error;
  }
};

// Export methods for admin panel
export { addDoc, updateDoc, deleteDoc, collection, doc, setDoc };